import {z} from 'zod';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';

const MIN_DISCOUNT = 0;
const MAX_DISCOUNT_PERCENTAGE = 100;

// A cleared control emits null (and a fresh form starts undefined); a type-level failure
// ABORTS the object parse, which silently skips superRefine and every cross-field rule in
// it — so required fields stay nullish here and required-ness lives in superRefine.
const clearableString = z.string().trim().nullish();
const optionIdsSchema = z.array(z.string().trim().nullable()).optional();

/**
 * The discount value is capped at 100 for every type except a fixed price, and at least
 * one of the two variant switches has to stay on.
 */
export const IndividualMaterialPriceFormSchema = z
  .object({
    id: z.string().nullable().optional(),
    warehouseId: clearableString,
    manufacturerNumber: clearableString,
    manufacturerId: clearableString,
    serviceOrderVariantIds: optionIdsSchema,
    directSaleVariantIds: optionIdsSchema,
    discountSettingsType: z.enum([
      'DISCOUNT_SALE_PRICE',
      'DISCOUNT_MARGIN_PURCHASE_PRICE',
      'DISCOUNT_FIXED_PRICE',
    ]),
    discountSettingsValue: z
      .number()
      .min(MIN_DISCOUNT, i18n.t('general.validations.minValue', {min: MIN_DISCOUNT}))
      .nullish(),
    isServiceOrderVariant: z.boolean(),
    isDirectSaleVariant: z.boolean(),
    name: z.string().nullish(),
  })
  .superRefine((values, ctx) => {
    const addRequiredIssue = (path: string) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [path],
        message: i18n.t('general.validations.fieldIsRequired'),
      });

    if (isNilOrEmpty(values.warehouseId)) {
      addRequiredIssue('warehouseId');
    }

    if (isNilOrEmpty(values.manufacturerNumber)) {
      addRequiredIssue('manufacturerNumber');
    }

    if (isNilOrEmpty(values.manufacturerId)) {
      addRequiredIssue('manufacturerId');
    }

    if (isNil(values.discountSettingsValue)) {
      addRequiredIssue('discountSettingsValue');
    }

    if (
      values.discountSettingsType !== 'DISCOUNT_FIXED_PRICE' &&
      isNotNil(values.discountSettingsValue) &&
      values.discountSettingsValue > MAX_DISCOUNT_PERCENTAGE
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['discountSettingsValue'],
        message: i18n.t('general.validations.maxValue', {max: MAX_DISCOUNT_PERCENTAGE}),
      });
    }

    if (!values.isServiceOrderVariant && !values.isDirectSaleVariant) {
      ['isServiceOrderVariant', 'isDirectSaleVariant'].forEach((path) =>
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: i18n.t('entity.customerContract.labels.variantValidation'),
        })
      );
    }
  });

export type IndividualMaterialPriceFormValues = z.infer<typeof IndividualMaterialPriceFormSchema>;
